/**
 * Safe JSON persistence over a Storage-like backend.
 *
 * Browser storage is a per-device convenience that can be missing, blocked
 * (privacy modes, sandboxed frames), full, or hold corrupt data. Every access
 * here is wrapped in try/catch, so callers never see an exception: reads fall
 * back to `null`, writes report `false`. When no working localStorage exists
 * the app falls back to an in-memory store, so a session still works — it just
 * isn't remembered.
 *
 * Keys are namespaced `dropstack.*` and carry a version suffix (`.v1`) so a
 * future format change can migrate or ignore old data instead of misreading it.
 */

/** The subset of the Web Storage API we rely on (tests inject a Map-backed one). */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Every key the web app persists. Change the version suffix when a format changes incompatibly. */
export const STORAGE_KEYS = {
  settings: 'dropstack.settings.v1',
  progress: 'dropstack.progress.v1',
  purchases: 'dropstack.purchases.v1',
  /** API credentials `{ playerId, secret }` (ApiBackend). Name fixed by docs/GAME_DESIGN.md § 7. */
  auth: 'dropstack.auth',
  /** Last profile the server returned (ApiBackend offline fallback). */
  profileCache: 'dropstack.profile.v1',
  /** Local player, runs, sessions and added schools (OfflineDemoBackend). */
  offlineDb: 'dropstack.offline.v1',
  /** Parental-gate lockout after repeated wrong answers (services/parentalGate.ts). */
  gate: 'dropstack.gate.v1',
} as const;

/** Decoder that turns parsed JSON into a trusted value, or null when the shape is wrong. */
export type Decoder<T> = (raw: unknown) => T | null;

/** Typed, exception-free JSON access. */
export interface JsonStore {
  /** Parsed and decoded value, or null if missing, unreadable, corrupt or rejected by `decode`. */
  get<T>(key: string, decode: Decoder<T>): T | null;
  /** Serialise and store. Returns false when the value could not be stored. */
  set(key: string, value: unknown): boolean;
  remove(key: string): void;
}

/** In-memory StorageLike (tests, and the fallback when localStorage is unusable). */
export function createMemoryStorage(initial?: Iterable<[string, string]>): StorageLike {
  const map = new Map<string, string>(initial);
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, String(value));
    },
    removeItem: (key) => {
      map.delete(key);
    },
  };
}

/**
 * window.localStorage when it exists and actually works (probed with a write),
 * otherwise null. Some runtimes expose a `localStorage` object that throws or
 * lacks methods (Safari private mode, Node without --localstorage-file).
 */
export function browserStorage(): StorageLike | null {
  try {
    const ls: unknown = typeof localStorage === 'undefined' ? undefined : localStorage;
    if (!isStorageLike(ls)) return null;
    const probe = 'dropstack.__probe__';
    ls.setItem(probe, '1');
    ls.removeItem(probe);
    return ls;
  } catch {
    return null;
  }
}

function isStorageLike(x: unknown): x is StorageLike {
  if (typeof x !== 'object' || x === null) return false;
  const s = x as Partial<Record<keyof StorageLike, unknown>>;
  return typeof s.getItem === 'function' && typeof s.setItem === 'function' && typeof s.removeItem === 'function';
}

/** Wrap a StorageLike (null → nothing is stored, every read is null). */
export function createJsonStore(backend: StorageLike | null): JsonStore {
  return {
    get<T>(key: string, decode: Decoder<T>): T | null {
      if (!backend) return null;
      try {
        const text = backend.getItem(key);
        if (text === null) return null;
        return decode(JSON.parse(text) as unknown);
      } catch {
        return null; // blocked storage, corrupt JSON or a throwing decoder
      }
    },
    set(key: string, value: unknown): boolean {
      if (!backend) return false;
      try {
        backend.setItem(key, JSON.stringify(value));
        return true;
      } catch {
        return false; // quota exceeded, storage blocked, or unserialisable value
      }
    },
    remove(key: string): void {
      if (!backend) return;
      try {
        backend.removeItem(key);
      } catch {
        // Nothing useful to do: the stale value will simply be ignored or overwritten later.
      }
    },
  };
}

let appStore: JsonStore | null = null;

/**
 * The app-wide store: localStorage when usable, otherwise in-memory for this
 * page load. Created lazily so importing this module has no side effects.
 */
export function getAppStorage(): JsonStore {
  appStore ??= createJsonStore(browserStorage() ?? createMemoryStorage());
  return appStore;
}

// ───────────── Decoder helpers ─────────────

/** Narrow to a plain object (not null, not an array). */
export function isRecord(x: unknown): x is Record<string, unknown> {
  return typeof x === 'object' && x !== null && !Array.isArray(x);
}

export const isFiniteNumber = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
